import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ImagePlus, Search, Users, UserPlus, Newspaper, Pencil, Image as ImageIcon, MessageCircle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import ImageUpload from "../components/ImageUpload";
import Avatar from "../components/social/Avatar";
import FriendButton from "../components/social/FriendButton";
import PostCard from "../components/social/PostCard";
import { themeGradient } from "../lib/themes";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";

const BADGES = ["", "Milestone", "First Words", "Potty Trained", "Reading Star", "Lost a Tooth"];

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : true
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

function PersonRow({ person, onOpen, right }) {
  return (
    <div className="flex items-center gap-3" data-testid={`person-row-${person.user_id}`}>
      <Avatar user={person} size={40} onClick={() => onOpen(person.user_id)} testid={`person-avatar-${person.user_id}`} />
      <button onClick={() => onOpen(person.user_id)} className="flex-1 min-w-0 text-left">
        <div className="font-semibold text-slate-800 text-sm truncate hover:underline">{person.name}</div>
        {person.bio && <div className="text-xs text-slate-400 truncate">{person.bio}</div>}
      </button>
      {right}
    </div>
  );
}

export default function SocialFeed() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [me, setMe] = useState(null);
  const [posts, setPosts] = useState([]);
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [suggestions, setSuggestions] = useState([]);
  const [mobileTab, setMobileTab] = useState("feed");
  const [mediaOnly, setMediaOnly] = useState(false);

  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);

  const [text, setText] = useState("");
  const [image, setImage] = useState("");
  const [badge, setBadge] = useState("");
  const [showImg, setShowImg] = useState(false);

  const openUser = (id) => navigate(`/social/u/${id}`);

  const loadSocial = useCallback(async () => {
    try {
      const [meR, frR, reqR, sugR] = await Promise.all([
        api.get("/social/me"),
        api.get("/social/friends"),
        api.get("/social/requests"),
        api.get("/social/suggestions"),
      ]);
      setMe(meR.data);
      setFriends(frR.data);
      setRequests(reqR.data);
      setSuggestions(sugR.data);
    } catch (err) {
      console.error("social load failed", err);
    }
  }, []);

  const loadPosts = useCallback(async () => {
    const { data } = await api.get("/posts");
    setPosts(data);
  }, []);

  useEffect(() => { loadSocial(); loadPosts(); }, [loadSocial, loadPosts]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 1) { setResults(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/social/search`, { params: { q: term } });
        setResults(data);
      } catch (err) { console.error("search failed", err); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const createPost = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.post("/posts", { text, image, badge });
      setText(""); setImage(""); setBadge(""); setShowImg(false);
      toast.success("Shared with your friends! 💛");
      loadPosts(); loadSocial();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const like = async (post) => {
    const { data } = await api.post(`/posts/${post.post_id}/like`);
    setPosts((ps) => ps.map((p) => p.post_id === post.post_id ? { ...p, likes: data.likes } : p));
  };

  const comment = async (post, txt) => {
    const { data } = await api.post(`/posts/${post.post_id}/comment`, { text: txt });
    setPosts((ps) => ps.map((p) => p.post_id === post.post_id ? { ...p, comments: [...(p.comments || []), data] } : p));
  };

  const react = async (post, sticker) => {
    const { data } = await api.post(`/posts/${post.post_id}/react`, { sticker });
    setPosts((ps) => ps.map((p) => p.post_id === post.post_id ? { ...p, reactions: data.reactions } : p));
  };

  const deletePost = async (post) => {
    await api.delete(`/posts/${post.post_id}`);
    setPosts((ps) => ps.filter((p) => p.post_id !== post.post_id));
    toast.success("Post deleted");
  };

  const pinPost = async (post) => {
    const { data } = await api.post(`/posts/${post.post_id}/pin`);
    setPosts((ps) => ps.map((p) => p.author_user_id === post.author_user_id
      ? { ...p, pinned: p.post_id === post.post_id ? data.pinned : false } : p));
    toast.success(data.pinned ? "Pinned to your profile 📌" : "Unpinned");
  };

  const afterFriendAction = () => { loadSocial(); loadPosts(); };

  const messageFriend = async (userId) => {
    try {
      const { data } = await api.post("/threads/direct", { user_id: userId });
      navigate(`/messages?t=${data.thread_id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not open chat");
    }
  };

  const ProfileCard = (
    <div className="bg-white rounded-3xl border border-amber-100/60 shadow-lg overflow-hidden" data-testid="my-profile-card">
      <div className="h-20" style={me?.cover ? { backgroundImage: `url(${me.cover})`, backgroundSize: "cover", backgroundPosition: "center" } : { background: themeGradient(me?.theme) }} />
      <div className="px-5 pb-5 -mt-8">
        <Avatar user={me} size={64} className="ring-4 ring-white" testid="my-avatar" />
        <div className="mt-2 font-display font-bold text-lg text-slate-900">{me?.name}</div>
        {me?.bio ? <p className="text-sm text-slate-500 mt-1">{me.bio}</p> : <p className="text-sm text-slate-400 mt-1 italic">Add a short bio…</p>}
        <div className="flex gap-4 mt-3 text-sm">
          <span className="text-slate-600"><b className="text-slate-900">{me?.friends_count ?? 0}</b> friends</span>
          <span className="text-slate-600"><b className="text-slate-900">{me?.posts_count ?? 0}</b> posts</span>
        </div>
        <Button onClick={() => navigate("/social/profile")} data-testid="my-profile-btn"
          className="mt-4 w-full rounded-full text-white font-bold hover:opacity-90" style={{ backgroundColor: me?.theme?.primary || "#FF5A79" }}>
          <Pencil className="h-4 w-4 mr-1.5" /> My Profile
        </Button>
      </div>
    </div>
  );

  const RequestsPanel = (
    <div className="bg-white rounded-3xl border border-amber-100/60 shadow-lg p-5" data-testid="requests-panel">
      <div className="flex items-center gap-2 mb-4">
        <UserPlus className="h-5 w-5 text-[#FF5A79]" />
        <h3 className="font-display font-bold text-slate-800">Friend requests</h3>
        {requests.incoming.length > 0 && (
          <span className="ml-auto bg-[#FF5A79] text-white text-xs font-bold rounded-full px-2 py-0.5" data-testid="requests-count">{requests.incoming.length}</span>
        )}
      </div>
      {requests.incoming.length === 0 && requests.outgoing.length === 0 && (
        <p className="text-sm text-slate-400">No pending requests.</p>
      )}
      <div className="space-y-4">
        {requests.incoming.map((p) => (
          <PersonRow key={p.user_id} person={p} onOpen={openUser}
            right={<FriendButton userId={p.user_id} status="pending_in" size="sm" onChanged={afterFriendAction} />} />
        ))}
        {requests.outgoing.length > 0 && (
          <div className="pt-2">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Sent</div>
            <div className="space-y-4">
              {requests.outgoing.map((p) => (
                <PersonRow key={p.user_id} person={p} onOpen={openUser}
                  right={<FriendButton userId={p.user_id} status="pending_out" size="sm" onChanged={afterFriendAction} />} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const SuggestionsPanel = (
    <div className="bg-white rounded-3xl border border-amber-100/60 shadow-lg p-5" data-testid="suggestions-panel">
      <h3 className="font-display font-bold text-slate-800 mb-4">People you may know</h3>
      {suggestions.length === 0 ? (
        <p className="text-sm text-slate-400">No suggestions right now.</p>
      ) : (
        <div className="space-y-4">
          {suggestions.map((p) => (
            <PersonRow key={p.user_id} person={p} onOpen={openUser}
              right={<FriendButton userId={p.user_id} status="none" size="sm" onChanged={afterFriendAction} />} />
          ))}
        </div>
      )}
    </div>
  );

  const FriendsPanel = (
    <div className="bg-white rounded-3xl border border-amber-100/60 shadow-lg p-5" data-testid="friends-panel">
      <div className="flex items-center gap-2 mb-4">
        <Users className="h-5 w-5 text-[#2AB7CA]" />
        <h3 className="font-display font-bold text-slate-800">Friends</h3>
        <span className="ml-auto text-sm text-slate-400">{friends.length}</span>
      </div>
      {friends.length === 0 ? (
        <p className="text-sm text-slate-400">No friends yet — find families below!</p>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {friends.map((p) => (
            <PersonRow key={p.user_id} person={p} onOpen={openUser}
              right={<Button size="sm" className="rounded-full h-8 px-3 text-xs bg-[#9B5DE5] hover:bg-[#843fd6]" onClick={() => messageFriend(p.user_id)} data-testid={`message-friend-${p.user_id}`}><MessageCircle className="h-3.5 w-3.5 mr-1" />Message</Button>} />
          ))}
        </div>
      )}
    </div>
  );

  const SearchResults = results !== null && (
    <div className="bg-white rounded-3xl border border-amber-100/60 shadow-lg p-5" data-testid="search-results">
      <h3 className="font-display font-bold text-slate-800 mb-4">Results for “{q}”</h3>
      {results.length === 0 ? (
        <p className="text-sm text-slate-400">No families found.</p>
      ) : (
        <div className="space-y-4">
          {results.map((p) => (
            <PersonRow key={p.user_id} person={p} onOpen={openUser}
              right={<FriendButton userId={p.user_id} status={p.status} size="sm" onChanged={() => { afterFriendAction(); setQ(q); }} />} />
          ))}
        </div>
      )}
    </div>
  );

  const Composer = (
    <form onSubmit={createPost} className="bg-white rounded-3xl border border-amber-100/60 shadow-lg p-5" data-testid="post-composer">
      <div className="flex gap-3">
        <Avatar user={me} size={44} />
        <Textarea data-testid="post-text-input" value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Share a milestone, playdate or proud moment…" className="rounded-2xl border-slate-200 min-h-[64px]" />
      </div>
      {showImg && <div className="mt-3"><ImageUpload testid="post-image-upload" value={image} onChange={setImage} /></div>}
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <button type="button" onClick={() => setShowImg((v) => !v)} data-testid="toggle-image-btn"
          className="inline-flex items-center gap-1.5 rounded-full bg-sky-100 text-sky-700 px-3 py-2 text-sm font-semibold hover:bg-sky-200">
          <ImagePlus className="h-4 w-4" /> Photo
        </button>
        <select data-testid="post-badge-select" value={badge} onChange={(e) => setBadge(e.target.value)}
          className="rounded-full bg-amber-100 text-amber-700 px-3 py-2 text-sm font-semibold border-0">
          {BADGES.map((b) => <option key={b} value={b}>{b || "🏅 Add badge"}</option>)}
        </select>
        <Button type="submit" data-testid="submit-post-btn" className="ml-auto rounded-full bg-[#FF5A79] hover:bg-[#e04865] font-bold px-5">Share</Button>
      </div>
    </form>
  );

  const Feed = (
    <div className="space-y-6" data-testid="posts-feed">
      {Composer}
      <div className="flex justify-end">
        <button onClick={() => setMediaOnly((v) => !v)} data-testid="media-filter-toggle"
          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold border-2 transition ${mediaOnly ? "bg-[#2AB7CA] text-white border-transparent" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
          <ImageIcon className="h-4 w-4" /> Photos & videos only
        </button>
      </div>
      {(mediaOnly ? posts.filter((p) => p.image || p.video) : posts).map((p) => (
        <PostCard key={p.post_id} post={p} user={user} onLike={like} onComment={comment} onReact={react} onPin={pinPost} onDelete={deletePost} />
      ))}
      {(mediaOnly ? posts.filter((p) => p.image || p.video) : posts).length === 0 && (
        <p className="text-center text-slate-400 py-8">{mediaOnly ? "No photos or videos yet. 📸" : "No posts yet. Be the first to share something! 💬"}</p>
      )}
    </div>
  );

  const MOBILE_TABS = [
    { id: "feed", label: "Feed", icon: Newspaper },
    { id: "friends", label: "Friends", icon: Users },
    { id: "requests", label: "Requests", icon: UserPlus, count: requests.incoming.length },
  ];

  return (
    <div className="min-h-screen bg-[#FFFDF7] pb-16">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-2 mb-5">
          <h1 className="font-display font-bold text-3xl text-slate-900">Playground</h1>
          <span className="text-2xl">🎈</span>
        </div>
        {/* Search bar */}
        <div className="relative mb-6 max-w-xl">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input data-testid="social-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search families by name or email…" className="rounded-full h-12 pl-12 bg-white shadow-sm border-amber-100" />
        </div>

        {SearchResults && q.trim().length >= 1 ? (
          <div className="max-w-2xl">{SearchResults}</div>
        ) : isDesktop ? (
          <div className="grid grid-cols-[280px_1fr_300px] gap-6 items-start">
            <div className="space-y-6 sticky top-20">
              {ProfileCard}
              {FriendsPanel}
            </div>
            <div>{Feed}</div>
            <div className="space-y-6 sticky top-20">
              {RequestsPanel}
              {SuggestionsPanel}
            </div>
          </div>
        ) : (
          <>
            <div className="flex gap-1 bg-slate-100 rounded-full p-1 mb-6">
              {MOBILE_TABS.map(({ id, label, icon: Icon, count }) => (
                <button key={id} data-testid={`social-tab-${id}`} onClick={() => setMobileTab(id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 rounded-full py-2.5 text-sm font-bold transition ${mobileTab === id ? "bg-white shadow text-slate-800" : "text-slate-500"}`}>
                  <Icon className="h-4 w-4" /> {label}
                  {count > 0 && <span className="bg-[#FF5A79] text-white text-[10px] rounded-full px-1.5 py-0.5">{count}</span>}
                </button>
              ))}
            </div>
            <div className="mb-6">{ProfileCard}</div>
            <div className="space-y-6">
              {mobileTab === "feed" && Feed}
              {mobileTab === "friends" && <>{FriendsPanel}{SuggestionsPanel}</>}
              {mobileTab === "requests" && RequestsPanel}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
